import React, { useEffect, useState, useRef } from "react";
import { Header } from "../components/Header";
import { useI18n } from "../context/I18nContext";
import { api, useAuth } from "../context/AuthContext";
import { money, dateFmt } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { DashboardNav } from "../components/DashboardNav";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, PiggyBank, ChartLineUp, HandCoins, Percent, MagnifyingGlass, Plus, ArrowsLeftRight, ShieldCheck, UserPlus, Trash, PencilSimple, ClockCounterClockwise, Prohibit, CheckCircle, FilePdf, Coins, Vault, CalendarCheck, EnvelopeSimple, ArrowsClockwise, ChartBar, LinkSimple, Image, FileXls, UploadSimple, CaretUp, CaretDown, ShareNetwork, Copy, Bank, PaperPlaneTilt, Receipt, Truck, Trophy, Medal, Ticket, IdentificationCard, Cake, ChatCircleText, Question } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ReferralPanel } from "../components/ReferralPanel";
import { SavingsAccountsPanel } from "../components/SavingsAccountsPanel";
import { VerificationsPanel } from "../components/VerificationsPanel";
import { StoreAdminPanel } from "../components/StoreAdminPanel";
import { LoansPanel } from "../components/LoansPanel";
import { CapitalPanel } from "../components/CapitalPanel";
import { PortfolioPanel } from "../components/PortfolioPanel";
import { CollectionsPanel } from "../components/CollectionsPanel";
import { TransfersAdminPanel } from "../components/TransfersAdminPanel";
import { RemittancesPanel } from "../components/RemittancesPanel";
import { BranchRankingPanel } from "../components/BranchRankingPanel";
import { RafflePanel } from "../components/RaffleTickets";
import { CalendarPanel } from "../components/CalendarPanel";
import { IdDocumentsPanel } from "../components/IdDocumentsPanel";
import { NotificationLogPanel } from "../components/NotificationLogPanel";
import { LoanApplicationsPanel } from "../components/LoanApplicationsPanel";
import { provinceColor } from "../lib/provinces";
import {
  AccBadge,
  downloadBlob,
  AccountBlock,
  AddAccountDialog,
  AuditPanel,
  BirthdaysPanel,
  CarouselPanel,
  ClientCard,
  CollectionsSummaryCards,
  ContentPanel,
  CreateLoanDialog,
  DemographicsPanel,
  EditClientProfileDialog,
  EmployeeDialog,
  EmployeesPanel,
  LinkClientDialog,
  LinkUnlinkedDialog,
  LiquidateDialog,
  LiquidationsPanel,
  LoanPayDialog,
  ManageClientDialog,
  MyBranchPositionCard,
  OverviewPanel,
  PROFILE_FIELD_LABELS,
  PayrollDialog,
  PendingRemittancesCard,
  RatesPanel,
  RenewDialog,
  SUMMARY_DAY_LABELS,
  StaffFormDialog,
  StaffPanel,
  StatCard,
  SummaryCard,
  SummaryGoalBar,
  SummaryTrendChart,
  TeamPanel,
  TestimonialsPanel,
  FaqPanel,
  NotificationsPanel,
  TermDepositDialog,
  TermDepositsPanel,
  TxDialog,
  WhatsAppSettingsPanel,
} from "../components/AdminComponents";

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const can = (p) => (user?.permissions || []).includes(p);
  const [clients, setClients] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [clientTotal, setClientTotal] = useState(0);
  const [fullClients, setFullClients] = useState([]);
  const [rates, setRates] = useState({ savings_rate: 0, shares_rate: 0 });
  const [search, setSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [page, setPage] = useState(0);
  const [unlinkedCount, setUnlinkedCount] = useState(0);
  const [pendingApps, setPendingApps] = useState(0);
  const [lockCount, setLockCount] = useState(0);
  const [savingsMonthJump, setSavingsMonthJump] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAppsCount = () => {
    if (can("manage_loans")) api.get("/admin/loan-applications/count").then((r) => setPendingApps(r.data.count)).catch(() => {});
  };
  const PAGE_SIZE = 12;

  const load = () => {
    const calls = [];
    if (can("view_clients")) calls.push(api.get("/admin/client-stats").then((r) => setClientStats(r.data)).catch(() => {}));
    if (can("view_clients")) calls.push(api.get("/admin/unlinked-count").then((r) => setUnlinkedCount(r.data.count)).catch(() => {}));
    if (can("manage_loans")) calls.push(api.get("/admin/loan-applications/count").then((r) => setPendingApps(r.data.count)).catch(() => {}));
    calls.push(api.get("/admin/rates").then((r) => setRates(r.data)).catch(() => {}));
    if (can("manage_accounts")) calls.push(api.get("/admin/savings-locks?days=400").then((r) => setLockCount((r.data || []).filter((x) => (x.unlock_date || "").slice(0, 7) === new Date().toISOString().slice(0, 7)).length)).catch(() => {}));
    Promise.all(calls).finally(() => setLoading(false));
  };

  const loadClients = () => {
    if (!can("view_clients")) return;
    const params = new URLSearchParams({ q: search, skip: String(page * PAGE_SIZE), limit: String(PAGE_SIZE), filter: accountFilter, sort: sortBy });
    api.get(`/admin/clients/paged?${params.toString()}`).then((r) => { setClients(r.data.items); setClientTotal(r.data.total); }).catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { setPage(0); }, [search, accountFilter, sortBy]);
  useEffect(() => { const id = setTimeout(loadClients, 250); return () => clearTimeout(id); }, [search, accountFilter, sortBy, page]); // eslint-disable-line

  const fmtTotals = (obj) => {
    const keys = Object.keys(obj || {});
    if (keys.length === 0) return money(0, "DOP");
    return keys.map((k) => money(obj[k], k)).join(" · ");
  };

  const totalPages = Math.max(1, Math.ceil(clientTotal / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages - 1);

  const roleLabel = user?.role === "admin" ? t("super_admin") : t("staff");

  const navItems = [
    can("view_clients") && { key: "clients", label: "clients", icon: Users, badge: unlinkedCount },
    can("view_clients") && { key: "birthdays", label: "birthdays_tab", icon: Cake },
    can("view_clients") && { key: "demographics", label: "demographics_tab", icon: ChartBar },
    can("view_clients") && { key: "overview", label: "overview", icon: ChartBar },
    can("manage_accounts") && { key: "deposits", label: "term_deposits", icon: Vault },
    can("manage_accounts") && { key: "savings", label: "savings_tab", icon: PiggyBank },
    can("view_clients") && { key: "verifications", label: "verif_tab", icon: ShieldCheck },
    can("manage_loans") && { key: "loans", label: "loans_tab", icon: HandCoins },
    can("manage_loans") && { key: "loanapps", label: "loan_applications_tab", icon: HandCoins, badge: pendingApps },
    can("manage_loans") && { key: "portfolio", label: "portfolio_tab", icon: ChartBar },
    can("manage_transactions") && { key: "collections", label: "collections_tab", icon: Receipt },
    can("manage_transactions") && { key: "remittances", label: "remittances_tab", icon: Truck },
    can("manage_transactions") && { key: "ranking", label: "ranking_tab", icon: Trophy },
    can("manage_transactions") && { key: "raffle", label: "raffle_tab", icon: Ticket },
    can("view_clients") && { key: "alltransfers", label: "transfers_tab", icon: PaperPlaneTilt },
    can("view_clients") && { key: "documents", label: "documents_tab", icon: IdentificationCard },
    can("manage_rates") && { key: "rates", label: "manage_rates", icon: Percent },
    can("manage_rates") && { key: "capital", label: "capital_tab", icon: Bank },
    can("manage_content") && { key: "content", label: "manage_content_tab", icon: Image },
    can("manage_content") && { key: "tienda", label: "Tienda", icon: Receipt },
    can("manage_content") && { key: "carousel", label: "carousel_tab", icon: Image },
    can("manage_content") && { key: "team", label: "team_tab", icon: Users },
    can("manage_content") && { key: "testimonials", label: "testimonials_tab", icon: ChatCircleText },
    can("manage_content") && { key: "faq", label: "faq_tab", icon: Question },
    can("manage_content") && { key: "notifications", label: "notifications_tab", icon: EnvelopeSimple },
    can("manage_accounts") && { key: "referral", label: "referral_tab", icon: ShareNetwork },
    can("manage_staff") && { key: "staff", label: "manage_staff_tab", icon: ShieldCheck },
    can("manage_staff") && { key: "empleados", label: "employees_tab", icon: Users },
    can("manage_staff") && { key: "whatsapp", label: "whatsapp_settings", icon: PaperPlaneTilt },
    can("manage_accounts") && { key: "liquidaciones", label: "liquidations_tab", icon: Coins },
    can("view_audit") && { key: "audit", label: "audit_log", icon: ClockCounterClockwise },
    can("view_audit") && { key: "notiflog", label: "notif_log_tab", icon: EnvelopeSimple },
    { key: "calendar", label: "calendar_tab", icon: CalendarCheck },
  ].filter(Boolean);

  const defaultSection = navItems[0]?.key || "clients";
  const [section, setSection] = useState(defaultSection);
  const activeSection = navItems.find((n) => n.key === section) ? section : defaultSection;

  useEffect(() => {
    if ((activeSection === "overview" || activeSection === "referral") && can("view_clients") && fullClients.length === 0) {
      api.get("/admin/clients").then((r) => setFullClients(r.data)).catch(() => {});
    }
  }, [activeSection]); // eslint-disable-line

  return (
    <div className="App bg-[#F8FAFC] min-h-screen">
      <Header />
      <DashboardNav items={navItems} active={activeSection} onChange={setSection} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl font-bold text-emerald-900" data-testid="admin-title">{t("admin_panel")}</h1>
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid="admin-role-badge">{roleLabel}</span>
        </div>

        {/* Stats */}
        {can("view_clients") && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testId="stat-total-clients" icon={Users} label={t("total_clients")} value={clientStats?.total_clients ?? 0} />
            <StatCard testId="stat-total-savings" icon={PiggyBank} label={t("total_savings")} value={fmtTotals(clientStats?.savings)} />
            <StatCard testId="stat-total-shares" icon={ChartLineUp} label={t("total_shares")} value={fmtTotals(clientStats?.shares)} />
            <StatCard testId="stat-active-loans" icon={HandCoins} label={t("active_loans")} value={clientStats?.active_loans ?? 0} />
          </div>
        )}

        {can("manage_accounts") && lockCount > 0 && (
          <button data-testid="unlock-alert" onClick={() => { setSavingsMonthJump(new Date().toISOString().slice(0, 7)); setSection("savings"); }} className="mt-4 w-full flex items-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm font-semibold transition-colors">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-200 text-amber-800 font-bold">{lockCount}</span>
            {t("unlock_alert_text")}
          </button>
        )}

        {user?.role === "staff" && user?.branch && <MyBranchPositionCard branch={user.branch} onOpen={() => setSection("ranking")} />}

        {can("manage_transactions") && <CollectionsSummaryCards onOpen={() => setSection("collections")} />}

        {can("manage_transactions") && <PendingRemittancesCard onOpen={() => setSection("remittances")} />}

        <div className="mt-8">
          {/* Clients section */}
          {activeSection === "clients" && can("view_clients") && (
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-5">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="client-search" placeholder={t("search_client")} className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="flex gap-1.5 flex-wrap" data-testid="client-filters">
                  {[["all", "filter_all_members"], ["no_savings", "filter_no_savings"], ["no_shares", "filter_no_shares"], ["no_loan", "filter_no_loan"]].map(([k, lbl]) => (
                    <button key={k} data-testid={`filter-${k}`} onClick={() => setAccountFilter(k)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${accountFilter === k ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
                      {t(lbl)}
                    </button>
                  ))}
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="client-sort" className="h-9 w-auto min-w-[150px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">{t("sort_name")}</SelectItem>
                    <SelectItem value="recent">{t("sort_recent")}</SelectItem>
                    <SelectItem value="oldest">{t("sort_oldest")}</SelectItem>
                  </SelectContent>
                </Select>
                {can("view_clients") && (
                  <Button data-testid="export-clients-btn" size="sm" variant="outline" onClick={() => downloadBlob("/admin/clients-export.xlsx", "socios.xlsx")} className="h-9 gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileXls size={15} weight="bold" /> Excel</Button>
                )}
                {can("view_clients") && (
                  <Button data-testid="export-clients-pdf-btn" size="sm" variant="outline" onClick={() => downloadBlob("/admin/clients-export.pdf", "socios.pdf")} className="h-9 gap-1 border-red-200 text-red-700 hover:bg-red-50"><FilePdf size={15} weight="bold" /> PDF</Button>
                )}
              </div>
              {loading ? (
                <p className="text-slate-500 py-6">{t("loading")}</p>
              ) : clients.length === 0 ? (
                <p className="text-slate-400 py-10 text-center" data-testid="clients-empty">{t("no_clients_found")}</p>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-cards">
                    {clients.map((c) => <ClientCard key={c.user.id} client={c} can={can} onDone={() => { load(); loadClients(); }} />)}
                  </div>
                  <div className="mt-6 flex items-center justify-center gap-4">
                    {totalPages > 1 && (
                      <div className="flex items-center gap-4" data-testid="clients-pagination">
                        <Button data-testid="clients-prev" size="sm" variant="outline" disabled={pageClamped === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">{t("prev")}</Button>
                        <span className="text-sm text-slate-500 font-mono-num">{pageClamped + 1} / {totalPages}</span>
                        <Button data-testid="clients-next" size="sm" variant="outline" disabled={pageClamped >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">{t("next")}</Button>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-center text-xs text-slate-400" data-testid="clients-count">{clientTotal} {t("clients").toLowerCase()}</p>
                </>
              )}
            </div>
          )}

          {/* Overview section */}
          {activeSection === "overview" && can("view_clients") && (
            <OverviewPanel clients={fullClients} />
          )}

          {activeSection === "birthdays" && can("view_clients") && (
            <BirthdaysPanel />
          )}

          {activeSection === "demographics" && can("view_clients") && (
            <DemographicsPanel onFilter={(term) => { setSearch(term); setSection("clients"); }} />
          )}

          {/* Rates section */}
          {activeSection === "rates" && can("manage_rates") && (
            <RatesPanel rates={rates} onSaved={(r) => { setRates(r); load(); }} />
          )}

          {/* Content section */}
          {activeSection === "content" && can("manage_content") && (
            <ContentPanel />
          )}

          {activeSection === "carousel" && can("manage_content") && (
            <CarouselPanel />
          )}

          {activeSection === "team" && can("manage_content") && (
            <TeamPanel />
          )}

          {activeSection === "testimonials" && can("manage_content") && (
            <TestimonialsPanel />
          )}

          {activeSection === "faq" && can("manage_content") && (
            <FaqPanel />
          )}

          {activeSection === "notifications" && can("manage_content") && (
            <NotificationsPanel />
          )}

          {/* Referral section */}
          {activeSection === "referral" && can("manage_accounts") && (
            <ReferralPanel clients={fullClients} />
          )}

          {/* Term deposits section */}
          {activeSection === "deposits" && can("manage_accounts") && (
            <TermDepositsPanel />
          )}

          {activeSection === "savings" && can("manage_accounts") && (
            <SavingsAccountsPanel initialMonth={savingsMonthJump} />
          )}

          {activeSection === "verifications" && can("view_clients") && (
            <VerificationsPanel />
          )}

          {activeSection === "tienda" && can("manage_content") && (
            <StoreAdminPanel />
          )}

          {activeSection === "loans" && can("manage_loans") && (
            <LoansPanel />
          )}

          {activeSection === "loanapps" && can("manage_loans") && (
            <LoanApplicationsPanel onChanged={refreshAppsCount} />
          )}

          {activeSection === "capital" && can("manage_rates") && (
            <CapitalPanel />
          )}

          {activeSection === "empleados" && can("manage_staff") && <EmployeesPanel />}
          {activeSection === "whatsapp" && can("manage_staff") && <WhatsAppSettingsPanel />}
          {activeSection === "liquidaciones" && can("manage_accounts") && <LiquidationsPanel />}

          {activeSection === "portfolio" && can("manage_loans") && (
            <PortfolioPanel />
          )}

          {activeSection === "collections" && can("manage_transactions") && (
            <CollectionsPanel />
          )}

          {activeSection === "remittances" && can("manage_transactions") && (
            <RemittancesPanel />
          )}

          {activeSection === "ranking" && can("manage_transactions") && (
            <BranchRankingPanel />
          )}

          {activeSection === "raffle" && can("manage_transactions") && (
            <RafflePanel />
          )}

          {activeSection === "alltransfers" && can("view_clients") && (
            <TransfersAdminPanel />
          )}

          {/* Staff section */}
          {activeSection === "staff" && can("manage_staff") && (
            <StaffPanel myPermissions={user?.permissions || []} />
          )}

          {/* Audit section */}
          {activeSection === "audit" && can("view_audit") && (
            <AuditPanel />
          )}

          {activeSection === "notiflog" && can("view_audit") && (
            <NotificationLogPanel />
          )}

          {activeSection === "calendar" && (
            <CalendarPanel canPublish={can("manage_content")} />
          )}

          {activeSection === "documents" && can("view_clients") && (
            <IdDocumentsPanel />
          )}
        </div>
      </div>
    </div>
  );
}
